/**
 * BillingPolicySettingsSection — injected into Settings via render-prop
 * (Prompt 11F.8) — Settings must not import from Invoices directly (ESLint
 * feature-boundary rule), same pattern as LeavePolicySettingsSection.
 */

import React from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { Card, Toggle } from '@/components/common';
import { useTenant } from '@core/tenant/TenantContext';
import { useBillingConfig, useUpdateBillingConfig } from '@features/invoices/api/config';
import type { ApiError } from '@core/api/client';

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.md} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  &:last-child {
    border-bottom: none;
  }
`;

const ToggleInfo = styled.div``;
const ToggleLabel = styled.div`
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  margin-bottom: 2px;
`;
const ToggleDescription = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ReadOnlyBanner = styled.div`
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

export const BillingPolicySettingsSection: React.FC = () => {
  const { isAllMode } = useTenant();
  const { data: config, isLoading } = useBillingConfig();
  const updateConfig = useUpdateBillingConfig();

  const toggle = async (key: 'auto_create_entitlements_on_paid' | 'void_cascades_to_entitlements') => {
    const current = config?.flags[key] ?? true;
    try {
      await updateConfig.mutateAsync({ [key]: !current });
      toast.success('Billing policy updated.');
    } catch (err) {
      toast.error((err as ApiError).message || 'Could not update billing policy.');
    }
  };

  return (
    <Card padding="lg" data-testid="candeza-settings-section-billing">
      <h3 style={{ marginTop: 0 }}>Billing</h3>
      <p style={{ fontSize: 14, color: '#64748b', marginTop: 0 }}>
        GST rate and registration live under Organisation &amp; Tax. These flags control what
        happens automatically when a payment is recorded or voided.
      </p>

      {isAllMode && (
        <ReadOnlyBanner data-testid="candeza-billing-all-mode-banner">
          Select a school to edit Billing settings.
        </ReadOnlyBanner>
      )}

      <ToggleRow>
        <ToggleInfo>
          <ToggleLabel>Auto-create entitlements on paid</ToggleLabel>
          <ToggleDescription>
            When on, a payment that fully settles an invoice auto-creates entitlements for any
            line item that references a package.
          </ToggleDescription>
        </ToggleInfo>
        <Toggle
          active={config?.flags.auto_create_entitlements_on_paid ?? true}
          onToggle={() => toggle('auto_create_entitlements_on_paid')}
          disabled={isAllMode || isLoading || updateConfig.isPending}
          data-testid="candeza-billing-auto-create-entitlements"
        />
      </ToggleRow>

      <ToggleRow>
        <ToggleInfo>
          <ToggleLabel>Void cascades to entitlements</ToggleLabel>
          <ToggleDescription>
            When on, voiding a payment also cancels the entitlements it created (if unused).
          </ToggleDescription>
        </ToggleInfo>
        <Toggle
          active={config?.flags.void_cascades_to_entitlements ?? true}
          onToggle={() => toggle('void_cascades_to_entitlements')}
          disabled={isAllMode || isLoading || updateConfig.isPending}
          data-testid="candeza-billing-void-cascades"
        />
      </ToggleRow>
    </Card>
  );
};

export default BillingPolicySettingsSection;
